import cn from 'classnames';
import { find, isNil, upperCase } from 'lodash';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';

import DeleteItemPopconfirmIcon from '@/comp/pages/EnginePage/Engine/Common/DeleteItemPopconfirmIcon/DeleteItemPopconfirmIcon';
import TableHeaderCell from '@/comp/pages/EnginePage/Engine/Common/TableHeaderCell/TableHeaderCell';
import ValidationTooltip from '@/comp/pages/EnginePage/Engine/Common/ValidationTooltip/ValidationTooltip';
import { EngineSubstepsEnum, EngineStepsEnum } from '@/store/Engine/constants';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { maxLength, onlyDigits } from '@/utils/util-functions';

import { Input } from './styled';
import { LONG_DASH } from '../../EngineVersionUtils';

const PressureRangesTable = observer(
  ({
    hasTriedToSubmit,
    selectedSubstepName,
  }: {
    hasTriedToSubmit?: boolean;
    selectedSubstepName?: EngineSubstepsEnum;
  }) => {
    const { t } = useTranslation();
    const { tunit } = useMeasurement();
    const { engineStore } = useStores();
    const {
      mainRangesConfiguration: { flowLevels, pressureLevels, unit },
      validationMessages,
    } = engineStore;

    const [selectedFlowRangeIndex] = useState(0);

    const selectedFlowPressureRanges = useMemo(
      () => pressureLevels.getPressureRangesByFlow(flowLevels.ranges?.[selectedFlowRangeIndex]?.key),
      [pressureLevels.ranges, flowLevels.ranges, selectedFlowRangeIndex],
    );

    const onRangeFieldChange =
      (key, field) =>
      ({ target: { value } }) => {
        pressureLevels.setRangeField(key, field, value, flowLevels.ranges?.[selectedFlowRangeIndex]?.key);
        if (hasTriedToSubmit)
          engineStore.validate(
            EngineStepsEnum.MainRangesConfiguration,
            EngineSubstepsEnum.PressureLevels,
            flowLevels.ranges?.[selectedFlowRangeIndex]?.key,
            `${field}-${key}`,
          );
      };

    const onDeleteRangeClick = key => () =>
      pressureLevels.deleteRange(key, flowLevels.ranges?.[selectedFlowRangeIndex]?.key);

    const onUpperBoundKeyPress = event => onlyDigits(event);

    const columns = [
      {
        dataIndex: 'name',
        key: 'name',
        title: <TableHeaderCell titleKey="name" />,
        width: '24%',
        render: name => (
          <span
            className={cn('range-name', selectedSubstepName === EngineSubstepsEnum.MainRangesResult && 'read-only')}
          >
            {name}
          </span>
        ),
      },
      {
        dataIndex: 'lowerBound',
        key: 'lowerBound',
        title: <TableHeaderCell titleKey="lowerBoundPressure" className="header-cell" />,
        width: '38%',
        render: (lowerBound, { key }) => (
          <div className="input-flex-row">
            <div
              className={cn(
                'value-unit',
                engineStore.hasFinishedWizard &&
                  (isNil(lowerBound) || lowerBound !== pressureLevels.getOldRangeValues(key)?.lowerBound) &&
                  'edited',
              )}
            >
              <Input
                value={lowerBound}
                onChange={onRangeFieldChange(key, 'lowerBound')}
                {...(!engineStore.hasFinishedWizard && { placeholder: LONG_DASH })}
                bordered={false}
                disabled
                $hasBeenEdited={
                  engineStore.hasFinishedWizard && lowerBound !== pressureLevels.getOldRangeValues(key)?.lowerBound
                }
                $valueLength={`${lowerBound ?? ''}`?.length}
              />
              {lowerBound ? <span className="input-unit">{tunit('bar', upperCase(unit))}</span> : null}
            </div>
          </div>
        ),
      },
      {
        dataIndex: 'upperBound',
        key: 'upperBound',
        title: <TableHeaderCell titleKey="upperBoundPressure" className="header-cell" />,
        width: '38%',
        render: (upperBound, { key }, index) => {
          const message = find(validationMessages, { key, field: 'upperBound', focused: true })?.message;
          return (
            <div className="upper-bound">
              <div className="input-flex-row">
                <div
                  className={cn(
                    'value-unit',
                    engineStore.hasFinishedWizard &&
                      (isNil(upperBound) || upperBound !== pressureLevels.getOldRangeValues(key)?.upperBound) &&
                      'edited',
                  )}
                >
                  <ValidationTooltip message={hasTriedToSubmit && message} tArgs={{ ranges: 'pressureRanges' }}>
                    <Input
                      value={upperBound}
                      onChange={onRangeFieldChange(key, 'upperBound')}
                      {...(!engineStore.hasFinishedWizard && { placeholder: t('addUpperRange') })}
                      bordered={false}
                      count={maxLength(4)}
                      $shouldUnderline
                      disabled={
                        upperBound === Infinity ||
                        engineStore.isReadOnly ||
                        selectedSubstepName === EngineSubstepsEnum.MainRangesResult
                      }
                      onKeyPress={onUpperBoundKeyPress}
                      $hasBeenEdited={
                        engineStore.hasFinishedWizard &&
                        upperBound !== pressureLevels.getOldRangeValues(key)?.upperBound
                      }
                      $valueLength={`${upperBound ?? ''}`?.length}
                      $isEditable
                    />
                  </ValidationTooltip>
                  {upperBound && upperBound !== Infinity ? (
                    <span
                      className={cn(
                        'input-unit',
                        !engineStore.isReadOnly &&
                          selectedSubstepName !== EngineSubstepsEnum.MainRangesResult &&
                          'editable',
                      )}
                    >
                      {tunit('bar', upperCase(unit))}
                    </span>
                  ) : null}
                </div>
              </div>
              {index > 0 && !engineStore.isReadOnly && selectedSubstepName !== EngineSubstepsEnum.MainRangesResult && (
                <DeleteItemPopconfirmIcon itemName={t('thisPressureLevel')} onConfirm={onDeleteRangeClick(key)} />
              )}
            </div>
          );
        },
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={selectedFlowPressureRanges}
        pagination={false}
        {...(selectedFlowPressureRanges?.length > 6 &&
          selectedSubstepName !== EngineSubstepsEnum.MainRangesResult && {
            scroll: { y: 300 },
          })}
        className="table"
      />
    );
  },
);

export default PressureRangesTable;
